import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

export default function ArticleEditor({ mode = "new" }) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [form, setForm] = useState({
    title: "",
    summary: "",
    cover_image_url: "",
    tags: [],
    body: "",
  });
  const [articleId, setArticleId] = useState(null);
  const [tagOptions, setTagOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(mode === "new");

  useEffect(() => {
    api.get("/article-tags").then(({ data }) => setTagOptions(data));
  }, []);

  useEffect(() => {
    if (mode === "edit" && slug) {
      api.get(`/articles/${slug}`).then(({ data }) => {
        setForm({
          title: data.title,
          summary: data.summary,
          cover_image_url: data.cover_image_url || "",
          tags: data.tags || [],
          body: data.body,
        });
        setArticleId(data.article_id);
        setLoaded(true);
      });
    }
  }, [mode, slug]);

  const toggleTag = (t) => {
    setForm((f) => ({
      ...f,
      tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.body.trim() || form.body === "<br>") {
      setError("İçerik boş olamaz.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      let result;
      if (mode === "new") {
        ({ data: result } = await api.post("/articles", form));
      } else {
        ({ data: result } = await api.patch(`/articles/${articleId}`, form));
      }
      toast.success(mode === "new" ? "Yazı yayınlandı" : "Yazı güncellendi");
      navigate(`/yazilar/${result.slug}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return (
      <div className="k-container py-16">
        <div className="k-card h-96 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="k-container py-12 md:py-16">
      <div className="mx-auto max-w-3xl">
        <h1 className="font-display text-5xl text-ink-900" data-testid="article-editor-title">
          {mode === "new" ? "Yeni yazı" : "Yazıyı düzenle"}
        </h1>

        <form onSubmit={submit} className="k-card mt-8 space-y-6 p-8" data-testid="article-editor-form">
          <div>
            <label className="k-label">Başlık</label>
            <input
              className="k-input"
              minLength={3}
              maxLength={200}
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="article-title-input"
            />
          </div>
          <div>
            <label className="k-label">Kısa özet (2-3 cümle)</label>
            <textarea
              rows={3}
              maxLength={500}
              className="k-input resize-none"
              required
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              data-testid="article-summary-input"
            />
          </div>
          <div>
            <label className="k-label">Kapak görseli URL (opsiyonel)</label>
            <input
              className="k-input"
              value={form.cover_image_url}
              onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })}
              placeholder="https://…"
              data-testid="article-cover-input"
            />
            {form.cover_image_url && (
              <img
                src={form.cover_image_url}
                alt="Kapak önizleme"
                className="mt-3 h-40 w-full rounded-xl object-cover"
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            )}
          </div>

          <div>
            <label className="k-label">Etiketler</label>
            <div className="flex flex-wrap gap-2">
              {tagOptions.map((t) => {
                const active = form.tags.includes(t.slug);
                return (
                  <button
                    key={t.slug}
                    type="button"
                    onClick={() => toggleTag(t.slug)}
                    data-testid={`article-tag-${t.slug}`}
                    className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
                      active
                        ? "border-sage-500 bg-sage-500 text-cream-50"
                        : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
                    }`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="k-label">İçerik</label>
            <RichTextEditor
              value={form.body}
              onChange={(html) => setForm((f) => ({ ...f, body: html }))}
            />
          </div>

          {error && (
            <div className="rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button type="submit" disabled={busy} className="k-btn-primary disabled:opacity-60" data-testid="article-submit-btn">
              {busy ? "Kaydediliyor…" : mode === "new" ? "Yazıyı yayınla" : "Kaydet"}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="k-btn-ghost"
              data-testid="article-cancel-btn"
            >
              Vazgeç
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
